import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text, Heading, Image } from 'rebass';
import { useHistory } from 'react-router-dom';
import { FiChevronDown, FiChevronUp, FiCopy, FiCheck, FiPlus, FiX } from 'react-icons/fi';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { RxDragHandleDots2 } from 'react-icons/rx';
import useCollectionCover from 'collections/hooks/cover';

import Modal from 'app/modal/v3';
import { TouchScrollable } from 'react-scrolllock';
import Button from 'app/buttons';
import Avatar from 'app/avatar';
import Loading from 'app/loading';
import { PrimaryButton } from '../components/FtButton';
import { FT_COLORS, FT_CONTENT_WIDTH } from '../theme';
import { MAX_VIDEO_IDEAS, defaultPersonalMessage } from '../constants';
import { patchTalkingFamilyTree, useTalkingFamilyTreeSWR } from '../api';
import { parseTalkingFamilyTree, pickInviteUrl } from '../helpers';
import invitationImage from '../assets/InvitationImage.png'
import { useIsSafariUser } from 'story-media/hooks';
// A "pick" is one selected idea, kept in order:
//   catalog: { id: 't:<topicId>', topicId }   custom: { id: 'c:<n>', custom }

// Filled/empty selection dot (with check when filled).
const Dot = ({ filled }) => (
  <Flex
    css="flex: 0 0 auto;"
    width="20px"
    height="20px"
    alignItems="center"
    justifyContent="center"
    sx={{
      borderRadius: '50%',
      border: '2px solid',
      borderColor: filled ? FT_COLORS.primary : FT_COLORS.dashedBorder,
      bg: filled ? FT_COLORS.primary : 'white',
    }}
  >
    {filled && (
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </Flex>
);
Dot.propTypes = { filled: PropTypes.bool };
Dot.defaultProps = { filled: false };

// ── Step 1: pick up to 3 ideas (catalog + custom) ───────────────────────────
const IdeasStep = ({
  sections, catalogIds, customPicks, selectedCount, toggle, onAddCustom, onRemovePick,
  letThemSpeak, setLetThemSpeak,
}) => {
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const atMax = selectedCount >= MAX_VIDEO_IDEAS;

  useEffect(() => {
    setOpen(sections.find((s) => s.ideas.find((i) => catalogIds.includes(i.key)))?.key);
  }, [catalogIds, sections]);

  const submitDraft = () => {
    if (!draft.trim() || atMax) return;
    onAddCustom(draft.trim());
    setDraft('');
  };

  const renderIdea = (idea) => {
    const isSelected = catalogIds.includes(idea.key);
    const disabled = (atMax && !isSelected) || letThemSpeak;
    return (
      <Flex
        as="button"
        type="button"
        key={idea.key}
        alignItems="center"
        width="100%"
        py="5px"
        // mb={1}
        disabled={disabled}
        onClick={() => toggle(idea.key)}
        sx={{
          gap: '10px',
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          border: 'none',
          borderRadius: '8px',
          bg: isSelected ? FT_COLORS.surface : 'transparent',
        }}
      >
        <Dot filled={isSelected} />
        <Text fontFamily="Poppins" fontSize="16px" color="#64748B">
          {idea.label}
        </Text>
      </Flex>
    );
  };

  return (
    <Box>
      <Text fontFamily="Poppins" fontSize="16px" color="#64748B" mb="20px">
        Choose up to {MAX_VIDEO_IDEAS} ideas to guide your family. We&apos;ll show them when they
        open the link, helping create warmer, more meaningful videos.
      </Text>

      {sections.map((section) => {
        const isOpen = open === section.key || section.ideas.find((i) => catalogIds.includes(i.key));
        if (section.key === "Video Message Ideas") return <Box pb={2}>{section.ideas.map(renderIdea)}</Box>; // Hide the "Video Message Ideas" section for now, as per the request.

        return (
          <Box key={section.key} mb={2} pb={isOpen ? 2 : 0} sx={{ border: '1px solid', borderColor: FT_COLORS.rowBorder, borderRadius: '12px' }}>
            <Flex
              as="button"
              type="button"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              p={2}
              onClick={() => setOpen(isOpen ? null : section.key)}
              sx={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <Text fontFamily="Poppins" fontWeight={600} fontSize="16px" color={FT_COLORS.muted}>
                {section.title}
              </Text>
              <Box as={isOpen ? FiChevronUp : FiChevronDown} width="26px" height="26px" color={FT_COLORS.muted} />
            </Flex>
            {isOpen && <Box px={3} >{section.ideas.map(renderIdea)}</Box>}
          </Box>
        );
      })}

      {/* Custom ideas the owner has added */}
      {customPicks.map((pick) => (
        <Flex
          key={pick.id}
          alignItems="center"
          width="100%"
          p={2}
          mb={1}
          sx={{ gap: '10px', borderRadius: '8px', bg: FT_COLORS.surface }}
        >
          <Dot filled />
          <Text flex="1 1 auto" fontFamily="Poppins" fontSize="13px" color={FT_COLORS.text}>
            {pick.custom}
          </Text>
          <Box
            as="button"
            type="button"
            onClick={() => onRemovePick(pick.id)}
            aria-label="Remove idea"
            sx={{ background: 'none', border: 'none', cursor: 'pointer', color: FT_COLORS.muted, display: 'inline-flex' }}
          >
            <FiX size={16} />
          </Box>
        </Flex>
      ))}

      {/* + Add your own idea (shown before "let them speak freely") */}
      <Flex
        alignItems="center"
        mb={2}
        p={1}
        onClick={() => inputRef.current && inputRef.current.focus()}
        sx={{
          border: '1px solid',
          borderColor: FT_COLORS.rowBorder,
          borderRadius: '10px',
          opacity: letThemSpeak ? 0.5 : 1,
          cursor: letThemSpeak || selectedCount >= MAX_VIDEO_IDEAS ? 'not-allowed' : 'text',
        }}
      >
        <Box as={FiPlus} color={FT_COLORS.primary} mx={1} css="flex: 0 0 auto;" />
        <input
          ref={inputRef}
          value={draft}
          disabled={letThemSpeak || selectedCount >= MAX_VIDEO_IDEAS}
          placeholder="Add your own idea"
          autoComplete="off"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitDraft(); } }}
          style={{ cursor: letThemSpeak || selectedCount >= MAX_VIDEO_IDEAS ? 'not-allowed' : 'text', flex: 1, width: '100%', minWidth: 0, border: 'none', outline: 'none', fontFamily: 'Poppins', fontSize: 13, background: 'transparent', padding: '8px 4px' }}
        />
        {draft.trim() && (
          <Button type="button" subtle onClick={submitDraft} style={{ color: FT_COLORS.primary }}>
            Add
          </Button>
        )}
      </Flex>

      <Flex
        as="button"
        type="button"
        alignItems="center"
        width="100%"
        p={2}
        onClick={() => setLetThemSpeak(!letThemSpeak)}
        sx={{
          gap: '10px',
          cursor: 'pointer',
          border: '1px dashed',
          borderColor: FT_COLORS.dashedBorder,
          borderRadius: '10px',
          bg: letThemSpeak ? FT_COLORS.surface : 'transparent',
        }}
      >
        <Dot filled={letThemSpeak} />
        <Text fontFamily="Poppins" fontSize="13px" color={FT_COLORS.text}>
          I&apos;d rather not suggest anything – let them speak freely
        </Text>
      </Flex>
    </Box>
  );
};

// ── Step 2: order the selected picks ────────────────────────────────────────
const OrderHandle = SortableHandle(() => (
  <Box mr={2} color={FT_COLORS.muted} css="cursor: grab; display: inline-flex;">
    <RxDragHandleDots2 size={18} />
  </Box>
));
const OrderItem = SortableElement(({ label, position }) => (
  <Flex alignItems="center" bg="white" p={2} mb={2} sx={{
    border: '1px solid', borderColor: FT_COLORS.rowBorder, borderRadius: '12px',
    boxShadow: '4px 4px 4px 0 rgba(133, 79, 155, 0.25)'
  }}>
    <Text fontFamily="Poppins" fontWeight={600} color={FT_COLORS.primary} mr={2}>
      {position}
    </Text>
    <OrderHandle />
    <Text fontFamily="Poppins" fontSize="13px" color={FT_COLORS.text}>
      {label}
    </Text>
  </Flex>
));
const OrderList = SortableContainer(({ items, labelFor }) => (
  <Box>
    {items.map((pick, index) => (
      <OrderItem key={pick.id} index={index} position={index + 1} label={labelFor(pick)} />
    ))}
  </Box>
));

// ── Step 3: personal message ────────────────────────────────────────────────
const MessageStep = ({ message, setMessage, babyName }) => (
  <Box>
    <Text fontFamily="Poppins" fontSize="14px" color="#64748B" mb={2}>
      This is what family reads when they open the link. Here&apos;s a starting point for you.
    </Text>
    <Flex justifyContent="space-between" alignItems="center" mb={1}>
      <Text fontFamily="Poppins" fontWeight={500} fontSize="14px">
        Message to the family <span style={{ color: 'red' }}>*</span>
      </Text>
      <Text
        as="button"
        type="button"
        onClick={() => setMessage(defaultPersonalMessage(babyName))}
        sx={{ background: 'none', border: 'none', color: FT_COLORS.primary, cursor: 'pointer', fontFamily: 'Poppins', fontSize: '13px' }}
      >
        Reset
      </Text>
    </Flex>
    {/*
      The mobile modal wraps its body in react-scrolllock's ScrollLock, which globally
      preventDefault()s touchmove and only re-allows it inside the body's TouchScrollable
      region — and only when that region itself overflows. On the Message step the body
      often fits, so the body's handler cancels every touchmove, including inside the
      textarea, so it can't scroll. Wrapping the textarea in its own TouchScrollable gives
      it a handler where `currentTarget` is the textarea: when its text overflows it stops
      propagation and scrolls natively. Touch-only — desktop wheel scroll is unaffected.
    */}
    <TouchScrollable>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={15}
        style={{
          width: '100%',
          color: '#8F8F8F',
          borderRadius: 12,
          padding: 12,
          border: `1px solid ${FT_COLORS.rowBorder}`,
          fontFamily: 'Poppins',
          fontSize: 14,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
        }}
      />
    </TouchScrollable>
  </Box>
);

// ── Step 4: review ──────────────────────────────────────────────────────────
const ReviewStep = ({ babyName, journalId, message, selected, labelFor }) => {
  const coverUrl = useCollectionCover({ id: journalId, type: 'child-journals' });

  return (
    <Box >
      <Text fontFamily="Poppins" fontSize="16px" color="#64748B" mb={3}>
        Here&apos;s what family will see. You can change any of this now.
      </Text>
      <Box sx={{ border: '1px solid', borderColor: FT_COLORS.rowBorder, borderRadius: '12px' }}>
        <Flex
          bg={FT_COLORS.surface}
          p={2}
          alignItems="center"
          sx={{ gap: '10px', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}
        >
          <Avatar name={babyName} fallbackUrl={coverUrl} size={28} border />
          <Text fontFamily="Bitter" fontWeight={500} fontSize="15px" color={FT_COLORS.text}>
            {`Be part of ${babyName}'s Talking Family Tree`}
          </Text>
        </Flex>
        <Box p={3}>
          <Text fontFamily="Poppins" fontSize="13px" color={FT_COLORS.text} css="white-space: pre-wrap;">
            {message}
          </Text>
          {selected.length > 0 && (
            <Box mt={3} p={2} bg="#D9EAF3" sx={{ borderRadius: '8px' }}>
              <Text fontFamily="Poppins" fontWeight={600} fontSize="13px" mb={2}>
                💡 What to say in your video
              </Text>
              {selected.map((pick) => (
                <Text key={pick.id} fontFamily="Poppins" fontSize="13px" color={FT_COLORS.muted} mb={1}>
                  • {labelFor(pick)}
                </Text>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
// ── Step 5: success ─────────────────────────────────────────────────────────
const SuccessStep = ({ inviteUrl, close }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard.writeText(inviteUrl || '');
      setCopied(true);
      setTimeout(() => {
        setCopied(false)
        close()
      }, 2000);
    } catch (e) { /* noop */ }
  };
  return (
    <Flex flexDirection="column" alignItems="center" textAlign="center" py={3}>
      <Image
        mb={2}
        src={invitationImage} width={338}
      />
      <Text fontFamily="Bitter" fontWeight={500} fontSize="20px" mb={2}>
        Your invitation is live and ready to share!
      </Text>
      <Text fontFamily="Poppins" fontSize="13px" color="#64748B" mb={3}>
        One link for the whole family. Share it anywhere.
      </Text>
      <Flex width="100%" alignItems="center" sx={{ gap: '8px' }}>
        <PrimaryButton onClick={copy} disabled={!inviteUrl}>
          {copied ? <FiCheck /> : <FiCopy />}
          {copied ? 'Copied' : 'Copy'}
        </PrimaryButton>
        <Box flex="1 1 auto" justifyContent="flex-start" p={2} sx={{ border: '1px solid', borderColor: FT_COLORS.rowBorder, borderRadius: '8px', overflow: 'hidden' }}>
          <Text fontFamily="Poppins" fontSize="13px" textAlign="left" color={FT_COLORS.muted} css="white-space: pre-wrap; overflow: hidden; text-overflow: ellipsis;">
            {inviteUrl || 'Generating link…'}
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
};

// ── Wizard shell ────────────────────────────────────────────────────────────
const InvitationWizard = ({ journalId, babyName, baseRoute, isDesktop }) => {
  const { replace } = useHistory();
  const close = () => replace(baseRoute);

  const { data: tftData, error: tftError, revalidate } = useTalkingFamilyTreeSWR(journalId);
  const parsed = useMemo(() => parseTalkingFamilyTree(tftData), [tftData]);
  const { sections } = parsed;
  const topicsLoading = !tftData && !tftError;

  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState([]); // ordered picks
  const [letThemSpeak, setLetThemSpeak] = useState(false);
  const [message, setMessage] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const customSeq = useRef(0);

  // Seed from the API once it loads (edit case).
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current || !tftData) return;
    initRef.current = true;
    if (parsed.selectedTopicIds.length) {
      setSelected(parsed.selectedTopicIds.map((id) => ({ id: `t:${id}`, topicId: id })));
    }
    setMessage(parsed.invitationCopy || defaultPersonalMessage(babyName));
    setInviteUrl(parsed.inviteUrl);
  }, [tftData, parsed, babyName]);

  // topic id → label for order/review rows.
  const labelFor = useMemo(() => {
    const map = {};
    parsed.topics.forEach((t) => { map[t.id] = t.label; });
    return (pick) => (pick.topicId ? (map[pick.topicId] || pick.topicId) : pick.custom);
  }, [parsed]);

  const catalogIds = useMemo(() => selected.filter((p) => p.topicId).map((p) => p.topicId), [selected]);
  const customPicks = useMemo(() => selected.filter((p) => p.custom), [selected]);

  const toggleCatalog = (topicId) => {
    setLetThemSpeak(false);
    setSelected((prev) => {
      if (prev.some((p) => p.topicId === topicId)) return prev.filter((p) => p.topicId !== topicId);
      if (prev.length >= MAX_VIDEO_IDEAS) return prev;
      return [...prev, { id: `t:${topicId}`, topicId }];
    });
  };

  const addCustom = (description) => {
    setLetThemSpeak(false);
    setSelected((prev) => {
      if (prev.length >= MAX_VIDEO_IDEAS) return prev;
      const id = `c:${customSeq.current}`;
      customSeq.current += 1;
      return [...prev, { id, custom: description }];
    });
  };

  const removePick = (id) => setSelected((prev) => prev.filter((p) => p.id !== id));

  const finish = async () => {
    setSaving(true);
    try {
      const res = await patchTalkingFamilyTree(journalId, {
        'invitation-copy': message,
        'selected-topics': letThemSpeak
          ? []
          : selected.map((p) => (p.topicId ? { 'topic-id': p.topicId } : { 'custom-description': p.custom })),
      });
      const attrs = res?.data?.initial?.data?.attributes || {};
      setInviteUrl(pickInviteUrl(attrs) || inviteUrl);
      revalidate();
      setStep(5);
    } catch (e) {
      setStep(5);
    } finally {
      setSaving(false);
    }
  };

  const BackIcon = (
    <svg style={{ minWidth: 'max-content' }} xmlns="http://www.w3.org/2000/svg" width="6" height="10" viewBox="0 0 6 10" fill="none">
      <path fillRule="evenodd" clipRule="evenodd" d="M5.70711 0.292893C6.09763 0.683417 6.09763 1.31658 5.70711 1.70711L2.41421 5L5.70711 8.29289C6.09763 8.68342 6.09763 9.31658 5.70711 9.70711C5.31658 10.0976 4.68342 10.0976 4.29289 9.70711L0.292894 5.70711C-0.0976306 5.31658 -0.0976306 4.68342 0.292894 4.29289L4.29289 0.292893C4.68342 -0.0976311 5.31658 -0.0976311 5.70711 0.292893Z" fill="#0E0E0E" />
    </svg>
  );
  const hasOrderStep = selected.length > 1 && !letThemSpeak;
  const titles = {
    1: 'Set Up Invitation',
    2: 'Move your ideas in the order you want family to answer them',
    3: 'Set Up Invitation',
    4: 'Review your invitation',
  };
  const subtitleByStep = { 1: 'Step 1: Video Message Ideas', 3: 'Step 2: Personal Message' };

  const back = step === 1 ? null : () => setStep((s) => (s === 3 && !hasOrderStep ? 1 : s - 1));

  const backButton = (onClick) => (
    <Button type="button" style={{ color: '#0E0E0E', background: 'transparent', border: 0, boxShadow: 'unset' }} onClick={onClick}>
      {BackIcon} Back
    </Button>
  );

  const isSafariUser = useIsSafariUser();

  let footer;
  if (step === 1) {
    footer = (
      <Flex justifyContent="space-between" width="100%">
        {backButton(close)}
        <PrimaryButton onClick={() => setStep(hasOrderStep ? 2 : 3)}>Next</PrimaryButton>
      </Flex>
    );
  } else if (step === 2) {
    footer = (
      <Flex justifyContent="space-between" width="100%">
        {backButton(() => setStep(1))}
        <PrimaryButton onClick={() => setStep(3)}>Next</PrimaryButton>
      </Flex>
    );
  } else if (step === 3) {
    footer = (
      <Flex justifyContent="space-between" width="100%">
        {backButton(() => setStep(hasOrderStep ? 2 : 1))}
        <PrimaryButton disabled={!message.trim()} onClick={() => setStep(4)}>Next</PrimaryButton>
      </Flex>
    );
  } else if (step === 4) {
    footer = (
      <Flex justifyContent="space-between" width="100%">
        {backButton(() => setStep(3))}
        <PrimaryButton loading={saving} onClick={finish}>Looks great! Get my link</PrimaryButton>
      </Flex>
    );
  } else {
    footer = (
      <Flex justifyContent="left" width="100%">
        {backButton(() => setStep(3))}
      </Flex>
    );
  }

  const stepContent = (
    <>
      {step === 1 && (topicsLoading ? (
        <Loading local />
      ) : (
        <IdeasStep
          sections={sections}
          catalogIds={catalogIds}
          customPicks={customPicks}
          selectedCount={selected.length}
          toggle={toggleCatalog}
          onAddCustom={addCustom}
          onRemovePick={removePick}
          letThemSpeak={letThemSpeak}
          setLetThemSpeak={setLetThemSpeak}
        />
      ))}
      {step === 2 && (
        <OrderList
          items={selected}
          labelFor={labelFor}
          useDragHandle
          lockAxis="y"
          onSortEnd={({ oldIndex, newIndex }) =>
            setSelected((prev) => {
              const next = [...prev];
              const [moved] = next.splice(oldIndex, 1);
              next.splice(newIndex, 0, moved);
              return next;
            })}
        />
      )}
      {step === 3 && <MessageStep message={message} setMessage={setMessage} babyName={babyName} />}
      {step === 4 && (
        <ReviewStep
          babyName={babyName}
          journalId={journalId}
          message={message}
          selected={letThemSpeak ? [] : selected}
          labelFor={labelFor}
        />
      )}
      {step === 5 && <SuccessStep inviteUrl={inviteUrl} close={close} />}
    </>
  );

  // Desktop: full page in the content area (no modal), footer pinned at the bottom.
  if (isDesktop) {
    return (
      <Flex flexDirection="column" css="min-height: 100vh;" width="100%" maxWidth={FT_CONTENT_WIDTH} mx="auto" px={4} py={4}>
        <Box flex="1 1 auto">
          {titles[step] && (
            <Heading
              fontFamily="Bitter"
              fontWeight={500}
              fontSize={['24px', '28px']}
              color={FT_COLORS.text}
              mb="20px"
            >
              {titles[step]}
            </Heading>
          )}
          {subtitleByStep[step] && (
            <Text fontFamily="Poppins" fontWeight={500} fontSize="18px" color="#000" mb="20px">
              {subtitleByStep[step]}
            </Text>
          )}
          <Box mt={2}>{stepContent}</Box>
        </Box>
        <Box pt={3} mt={4} sx={{ borderTop: '1px solid', borderColor: FT_COLORS.rowBorder }}>
          {footer}
        </Box>
      </Flex>
    );
  }

  // Mobile: modal overlay.
  return (
    <Modal
      title={titles[step] || ''}
      subtitle={subtitleByStep[step]}
      close={{ onClose: close }}
      // back={back ? { onBack: back } : {}}
      closeOnOutsideClick={false}
      footer={footer}
      height="100vh"
      shadeProps={{ pt: [0, 1] }}
      childSx={{ flex: 'unset !important' }}
    >
      <Box px={[3, 4]} py={2} pb={9}>
        {stepContent}
      </Box>
    </Modal>
  );
};

InvitationWizard.propTypes = {
  journalId: PropTypes.string.isRequired,
  babyName: PropTypes.string,
  baseRoute: PropTypes.string.isRequired,
  isDesktop: PropTypes.bool,
};

InvitationWizard.defaultProps = { babyName: 'your baby', isDesktop: false };

export default InvitationWizard;
